import { FC, useState, useEffect, useCallback, useRef } from 'react';

import { SideNavPopupContext } from './usePopupContact';

import { Portal } from '@reach/portal';
import { useHighlightPopupService } from './HighlighPopupService';
import observeRect from '@reach/observe-rect';
import { useRouter } from 'next/router';

import styles from './HighlightPopup.module.scss';

interface HighlightPopupProps {
  highlightId: string;
  delay?: number;
  onPopupClose?: () => void;
  showOverlay?: boolean;
  closeOnChangeRouter?: boolean;
}

const HighlightPopupOverlay: FC<React.PropsWithChildren<{ showOverlay: boolean }>> = ({
  children,
  showOverlay,
}) => {
  return showOverlay ? <div className={styles['overlay']}>{children}</div> : <>{children}</>;
};

/**
 * Popup to highlight the a specific side nav item
 */
const HighlightPopup: FC<React.PropsWithChildren<HighlightPopupProps>> = ({
  children,
  highlightId,
  delay,
  onPopupClose,
  showOverlay = false,
  closeOnChangeRouter = true,
}) => {
  const { events } = useRouter();
  const [openPopup, setOpenPopup] = useState(false);
  const { currentPopup, closePopup, updateCurrentPopupRect } = useHighlightPopupService();
  const currentRoute = useRef<string>();

  useEffect(() => {
    currentRoute.current = window.location.pathname;
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (highlightId === currentPopup) {
        setOpenPopup(true);
      }
    }, delay);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [highlightId, delay, currentPopup]);

  useEffect(() => {
    const sideNavLinks = window.document.querySelectorAll(`[data-highlight-id="${currentPopup}"]`);
    const sideNavLinkElement = sideNavLinks[0];
    if (!sideNavLinkElement) {
      return;
    }
    const rectObserver = observeRect(sideNavLinkElement, (rect) => {
      updateCurrentPopupRect(rect);
    });
    rectObserver.observe();

    return () => {
      rectObserver.unobserve();
    };
  }, [currentPopup, updateCurrentPopupRect]);

  const handleClosePopup = useCallback(() => {
    setOpenPopup(false);
    onPopupClose && onPopupClose();
    closePopup();
  }, [closePopup, onPopupClose]);

  useEffect(() => {
    const handleRouteChange = (pathname: string) => {
      const newURL = new URL(pathname, window.location.origin);
      if (newURL.pathname === currentRoute.current) {
        return;
      }

      if (!openPopup || !closeOnChangeRouter) {
        return;
      }

      closePopup();
    };

    events.on('routeChangeStart', handleRouteChange);

    return () => {
      events.off('routeChangeStart', handleRouteChange);
    };
  }, [events, closePopup, openPopup, closeOnChangeRouter]);

  return openPopup && highlightId === currentPopup ? (
    <Portal>
      <HighlightPopupOverlay showOverlay={showOverlay}>
        <SideNavPopupContext.Provider value={{ closePopup: handleClosePopup }}>
          {children}
        </SideNavPopupContext.Provider>
      </HighlightPopupOverlay>
    </Portal>
  ) : null;
};

export { HighlightPopup };
